import { useQueryData } from "@src/hooks/useQueryData";
import { byCode, removeCachedListItem } from "@src/hooks/queryListCache";
import {
    stockKanbanContainersServices,
    type StockKanbanContainer,
    type StockKanbanContainerContent,
    type StockKanbanContainerContentExtends,
    type StockKanbanContainerExtends,
} from "../../../../api/stockKanbanContainers";

/** Include карточки контейнера КАНБАН. */
export const STOCK_KANBAN_CONTAINER_INCLUDES = {
    include: ["StockObj", "Measure", "Division", "Section", "ComplectUser", "RemainderCells"] satisfies Array<
        keyof StockKanbanContainerExtends
    >,
} as const;

/** Include партий контейнера КАНБАН. */
export const STOCK_KANBAN_CONTENT_INCLUDES = {
    include: ["Lot", "Measure", "StorePlace"] satisfies Array<keyof StockKanbanContainerContentExtends>,
} as const;

/** Загружает карточку контейнера КАНБАН. */
export function useStockKanbanSupplyCardQuery(containerCode: number) {
    return useQueryData<StockKanbanContainer | null>({
        queryKey: ["stock", "kanbanSupply", containerCode, "card"],
        queryFn: ({ signal }) =>
            stockKanbanContainersServices.loadByCode(
                {
                    code: containerCode,
                    ...STOCK_KANBAN_CONTAINER_INCLUDES,
                },
                { signal },
            ),
        enabled: containerCode > 0,
        meta: { errorSource: "stock.kanbanSupply.card" },
    });
}

/** Результат загрузки партий контейнера КАНБАН. */
export interface UseStockKanbanSupplyContentsQueryResult {
    /** Выбранные партии контейнера. */
    content: StockKanbanContainerContent[];
    /** Признак фоновой загрузки. */
    isFetching: boolean;
    /** Перезагружает партии контейнера. */
    refetch: () => Promise<unknown>;
    /**
     * Заменяет весь список партий в cache (после create/update `addLot`).
     * @param contents актуальный состав контейнера
     */
    replaceContents: (contents: StockKanbanContainerContent[]) => void;
    /**
     * Удаляет партию из cache после remove.
     * @param contentCode код строки партии
     */
    removeContent: (contentCode: number) => void;
}

/** Загружает партии контейнера КАНБАН. */
export function useStockKanbanSupplyContentsQuery(containerCode: number): UseStockKanbanSupplyContentsQueryResult {
    const query = useQueryData<StockKanbanContainerContent[]>({
        queryKey: ["stock", "kanbanSupply", containerCode, "contents"],
        queryFn: async ({ signal }) => {
            const items = await stockKanbanContainersServices.content.load(
                {
                    filter: { ContainerCode: containerCode },
                    ...STOCK_KANBAN_CONTENT_INCLUDES,
                },
                { signal },
            );
            return items;
        },
        enabled: containerCode > 0,
        meta: { errorSource: "stock.kanbanSupply.contents" },
    });

    const replaceContents = (contents: StockKanbanContainerContent[]): void => {
        query.setData(contents);
    };

    const removeContent = (contentCode: number): void => {
        removeCachedListItem(query.setData, contentCode, byCode);
    };

    return {
        content: query.data ?? [],
        isFetching: query.isFetching,
        refetch: query.refetch,
        replaceContents,
        removeContent,
    };
}
